import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_BADGE_LISTING_FIELDS } from '../../config/r2rListingBadges';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';

import {
  avatarImageFieldVariants,
  listingCardAuthorUserFields,
  listingCardSponsorHighlightFields,
} from '../../util/avatarImageQueryFields';
import { LISTING_CARD_IMAGE_LIMIT, LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// Number of recent listings shown in the landing page section
const RECENT_LISTINGS_COUNT = 6;

// ================ Helper Functions ================ //

const resultIds = data =>
  data.data.filter(l => !l.attributes.deleted && l.attributes.state === 'published').map(l => l.id);

// ================ Async Thunks ================ //

const queryRecentListingsPayloadCreator = ({ config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    // Sharetribe sorts descending by default, so `pub_publishedAt` (no `-` prefix)
    // returns the newest first-went-live listings first.
    sort: 'pub_publishedAt',
    perPage: RECENT_LISTINGS_COUNT,
    // Only include listings that are actually available to purchase/book
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      ...new Set([
        'title',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'publicData.categoryLevel3',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        ...listingCardSponsorHighlightFields,
        // publicData fields required by R2R listing badges (e.g. WYSIWYG)
        ...LISTING_BADGE_LISTING_FIELDS,
        ...LISTING_CARD_MEDIA_FIELDS,
      ]),
    ],
    'fields.user': listingCardAuthorUserFields,
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      ...listingCardImageFields(variantPrefix),
      ...avatarImageFieldVariants,
    ],
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
    'limit.images': LISTING_CARD_IMAGE_LIMIT,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryRecentListings = createAsyncThunk(
  'LandingPage/queryRecentListings',
  queryRecentListingsPayloadCreator
);

// ================ Slice ================ //

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    recentListingIds: [],
    queryInProgress: false,
    queryError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryRecentListings.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryRecentListings.fulfilled, (state, action) => {
        state.recentListingIds = resultIds(action.payload.data);
        state.queryInProgress = false;
      })
      .addCase(queryRecentListings.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
      });
  },
});

export default landingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, anonymous/unauthorized users cannot query listings,
  // so we skip fetching to avoid errors and let the section degrade gracefully.
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

  if (!canFetchData) {
    return Promise.resolve();
  }

  return dispatch(queryRecentListings({ config }));
};
